import React, { useState, useEffect, useContext } from 'react';
import {
  Card,
  Stack,
  Button,
  Drawer,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/apps.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  addVideosById,
  generateRokuFeed,
  getListData,
  removeVideosById,
  deleteDataById,
  toggleMigratedStatus,
  regenerateCache,
} from '../../services/apps.service';
import { ACTIONS } from '../../config/const.config';
import SnackbarInfo from '../common/snackbar-info.component';
import FilterApps from './filter-app.component';
import replaceIdUrl from '../../utils/url.util';
import ROUTE_PATH from '../../config/routes.config';
import CreateApp from './create-app.component';
import ConfirmPopup from '../common/confirm-popup.component';
import { FiltersContext } from '../../context/filter.context';
import VideoAssignment from './video-assignment.component';
import useAccess from '../../hooks/access.hook';
import useFilteredActions from '../../hooks/filtered-action.hook';
import PreviewWebApp from './preview-web-app.component';
import {
  getLocalFilterValue,
  setLocalFilterValue,
} from '../../utils/local-cache.util';
import useAbortableRequest from '../../hooks/abortable-request.hook';

const ListContentApps = () => {
  const FILTERS_KEY = 'channels-filters';
  const columns = LISTING_COLUMNS;
  const actions = useFilteredActions('channel_settings', LISTING_ACTIONS);

  const { getSignal, isAbortError } = useAbortableRequest();

  const navigate = useNavigate();
  const { hasOperationAccess } = useAccess();

  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 100,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    error: false,
    loading: true,
    appliedFilters: {
      search: { name: 'Search', value: '' },
    },
    filtersCounter: 0,
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const { openFilter, handleToggleFilters } = useContext(FiltersContext);

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleApplyFilters = (selectedFilters) => {
    setLocalFilterValue(FILTERS_KEY, selectedFilters);
    let counter = 0;
    if (selectedFilters.search.value.trim()) counter += 1;

    setOptions({
      ...options,
      appliedFilters: {
        ...selectedFilters,
      },
      filtersCounter: counter,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleClearFilters = () => {
    setLocalFilterValue(FILTERS_KEY, {});
    setOptions({
      ...options,
      appliedFilters: {
        search: { name: 'Search', value: '' },
      },
      filtersCounter: 0,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    if (selectedAction === ACTIONS.EDIT.value) {
      const url = replaceIdUrl(
        ROUTE_PATH.CONTENT_CHANNEL_EDIT,
        selectedData.id
      );
      navigate(url);
      return;
    }
    if (selectedAction === ACTIONS.VIEW.value) {
      const url = replaceIdUrl(
        ROUTE_PATH.CHANNEL_VIEW_DETAILS,
        selectedData.id
      );
      navigate(url);
      return;
    }
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });
  };
  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleGenerateRokuFeed = () => {
    generateRokuFeed(doAction.data.id, {})
      .then(() => {
        handleActionCancel();
        handleRefreshData();
      })
      .catch((err) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            err.response?.data?.message ||
            'Something went wrong, please try again.',
        });
        handleActionCancel();
      });
  };

  const handleRegenerateCache = () => {
    regenerateCache(doAction.data.id, {})
      .then(() => {
        handleActionCancel();
        handleRefreshData();
      })
      .catch((err) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message:
            err.response?.data?.message ||
            'Something went wrong, please try again.',
        });
        handleActionCancel();
      });
  };

  const handleMigratedClick = (r, value) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r.id,
      value: value ? 1 : 0,
    };

    toggleMigratedStatus(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: `Migrated Status updated for ${r.title || r.code}.`,
        });
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleDeleteData = (id) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteDataById(id)
      .then(() => {
        handleActionSuccess('Channel deleted successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleDeleteFilter = (field) => {
    if (field === 'clear-all') {
      handleClearFilters();
      return;
    }
    const updatedFilters = { ...options.appliedFilters };
    updatedFilters[field] = {
      ...updatedFilters[field],
      value: Array.isArray(updatedFilters[field]?.value) ? [] : null || '',
    };
    setLocalFilterValue(FILTERS_KEY, updatedFilters);

    setOptions({
      ...options,
      appliedFilters: updatedFilters,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleVideoAdd = (data) =>
    addVideosById(doAction.data.id, data)
      .then((response) => response)
      .catch((error) => Promise.reject(error));

  const handleVideoRemove = (data) =>
    removeVideosById(doAction.data.id, data)
      .then((response) => response)
      .catch((error) => Promise.reject(error));

  useEffect(() => {
    const signal = getSignal();

    const localFilters = getLocalFilterValue(FILTERS_KEY);

    const params = [];
    params.push(`page=${options.page + 1}`);
    params.push(`perPage=${options.rowsPerPage}`);
    if (options.sortBy && options.sortOrder) {
      params.push(`sortBy=${options.sortBy}`);
      params.push(`sortOrder=${options.sortOrder}`);
    }
    const filters = Object.keys(localFilters).reduce((acc, key) => {
      acc[key] = localFilters[key].value;
      return acc;
    }, {});
    if (filters.search) {
      params.push(`q=${encodeURIComponent(filters.search)}`);
    }

    const uri = '';
    const paramsQuery = params.length > 0 ? `?${params.join('&')}` : '';
    getListData(uri + paramsQuery, signal)
      .then((res) => {
        if (!signal.aborted) {
          setOptions({
            ...options,
            loading: false,
            totalRows: res?.data?.totalRows || 0,
            rows: res?.data?.rows || [],
            appliedFilters: localFilters || {},
            error: false,
          });
        }
      })
      .catch((error) => {
        if (isAbortError(error)) {
          return;
        }

        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
        });
      });
  }, [options.reloadCounter]);

  return (
    <Box
      sx={{
        transition: 'margin 0.3s ease, width 0.3s ease',
        width: openFilter ? 'calc(100% - 300px)' : '100%',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 0, py: 2 }}
      >
        <div>
          {hasOperationAccess('channel_settings', 'create') && (
            <Button
              color="primary"
              variant="contained"
              startIcon={<Iconify icon="ic:round-add" />}
              onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
              sx={{ marginRight: 2 }}
            >
              Create New
            </Button>
          )}
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="octicon:video-16" />}
            onClick={() => handleActionSelect(ACTIONS.PREVIEW.value, null)}
          >
            Preview Web Channel
          </Button>
        </div>
        <div>
          <Button
            color="inherit"
            variant="contained"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            onClick={handleRefreshData}
            disabled={options.loading}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      {options.appliedFilters &&
        Object.values(options.appliedFilters).some(
          (filter) =>
            (Array.isArray(filter.value) && filter.value.length > 0) ||
            (!Array.isArray(filter.value) && filter.value)
        ) && (
          <Box mb={2}>
            <Typography variant="body1" fontWeight="bold" display="inline">
              Filters Applied:{' '}
              <Chip
                key="all"
                label="Clear all"
                onDelete={() => handleDeleteFilter('clear-all')}
                sx={{ ml: 1, color: 'red' }}
              />
            </Typography>
            {Object.entries(options.appliedFilters)
              .filter(
                ([, filter]) =>
                  (Array.isArray(filter.value) && filter.value.length > 0) ||
                  (!Array.isArray(filter.value) && filter.value)
              )
              .map(([field, filter]) => (
                <Chip
                  key={field}
                  label={filter.name || 'Active'}
                  onDelete={() => handleDeleteFilter(field)}
                  sx={{ ml: 1 }}
                />
              ))}
          </Box>
        )}

      <Card>
        <ListData
          columns={columns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={actions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          handleMigratedClick={handleMigratedClick}
          disabledSwitch={!hasOperationAccess('channel_settings', 'edit')}
        />
      </Card>

      <Drawer
        anchor="right"
        open={openFilter}
        variant="persistent"
        PaperProps={{
          sx: { width: 300, border: 'none', overflow: 'hidden' },
        }}
      >
        <FilterApps
          values={options.appliedFilters}
          onCancel={handleToggleFilters}
          onSuccess={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </Drawer>
      {doAction.action === ACTIONS.CREATE.value && (
        <CreateApp
          title="Create New Channel"
          copyChannelId={0}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}
      {doAction.action === ACTIONS.PREVIEW.value && (
        <PreviewWebApp
          title="Preview Web Channel"
          onCancel={handleActionCancel}
        />
      )}

      {doAction.action === ACTIONS.COPY_CHANNEL.value && (
        <CreateApp
          title={`Duplicate Channel - ${doAction.data.title}`}
          copyChannelId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {doAction.action === ACTIONS.GENERATE_ROKU_FEED.value && (
        <ConfirmPopup
          title="Generate Roku Feed"
          message={`Do you want to generate roku feed for ${doAction.data.title} channel?`}
          onCancel={handleActionCancel}
          onSuccess={() => handleGenerateRokuFeed()}
        />
      )}

      {doAction.action === ACTIONS.REGENERATE_CACHE.value && (
        <ConfirmPopup
          title="Regenerate Cache"
          message={`Do you want to regenerate cache for ${doAction.data.title} channel?`}
          onCancel={handleActionCancel}
          onSuccess={() => handleRegenerateCache()}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title="Delete Channel"
          message={`Do you want to delete ${doAction.data.title} channel? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={() => handleDeleteData(doAction.data.id)}
          successButtonText="Delete"
          cancelButtonText="Cancel"
          showConfirmationInput
          confirmationInputText="delete"
        />
      )}

      {doAction.action === ACTIONS.MANAGE_VIDEOS.value && (
        <VideoAssignment
          dataId={doAction.data.id}
          title={`Manage Videos for ${doAction.data.title} channel`}
          onCancel={handleActionCancel}
          onAddData={(data) => handleVideoAdd(data)}
          onRemoveData={(data) => handleVideoRemove(data)}
          from="channelVideoAssignment"
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Box>
  );
};

export default ListContentApps;
